/**
 * Touch controls for iPad and phone.
 *
 * Howard, 2026-09-07: "Can you make controls exactly like Minecraft controls?" — with a
 * screenshot of Minecraft Bedrock's mobile HUD. So this is that HUD: a D-pad of dark
 * translucent squares at one thumb, and a jump / sprint / sneak column at the other. It
 * supersedes the joystick Kate first described ("a circle and a small dot"); looking is
 * still a separate drag, on its own touch, so you can look and move at once.
 *
 * The attack button is still hers: "On iPad, there's something that has like a little
 * chomp icon on it. You press that, iPad phones. You press that and you can bite onto
 * something." When asked if it says "kill", she corrected it — "no, it's a button that
 * has like attack, attack on it." So the label is ATTACK and the icon is a chomp.
 *
 * Layout is configurable, because she said it is: "In settings, can you change like how
 * you use your controller on your iPad? Yes, you can."
 */
import type { LookControl } from './look'
import type { Settings, SettingsStore } from './settings'

/** Two taps on forward inside this window start a sprint, as in Minecraft. */
const DOUBLE_TAP_MS = 300

type PadDir = 'f' | 'b' | 'l' | 'r' | 'fl' | 'fr'

/** The 3×3 pad, row-major. The blank squares are null. */
const PAD_CELLS: ReadonlyArray<PadDir | null> = ['fl', 'f', 'fr', 'l', null, 'r', null, 'b', null]

const PAD_VECTORS: Record<PadDir, { forward: number; strafe: number }> = {
  f: { forward: 1, strafe: 0 },
  b: { forward: -1, strafe: 0 },
  l: { forward: 0, strafe: -1 },
  r: { forward: 0, strafe: 1 },
  fl: { forward: 1, strafe: -1 },
  fr: { forward: 1, strafe: 1 },
}

/** Arrow glyph rotation per cardinal square, degrees clockwise from straight up. */
const PAD_ANGLES: Record<'f' | 'b' | 'l' | 'r', number> = { f: 0, r: 90, b: 180, l: 270 }

/** Minecraft's glyphs are pixel art, so these are too: a 9×9 grid, X for a pixel. */
const ARROW_PIXELS = [
  '....X....',
  '...XXX...',
  '..XXXXX..',
  '.XXXXXXX.',
  '...XXX...',
  '...XXX...',
  '...XXX...',
  '...XXX...',
  '...XXX...',
]
/** Forward-right; forward-left is its mirror. */
const DIAGONAL_PIXELS = [
  '...XXXXXX',
  '......XXX',
  '.....XXXX',
  '....XXX.X',
  '...XXX..X',
  '..XXX....',
  '.XXX.....',
  'XXX......',
  'XX.......',
]
/** The sprint double chevron. */
const SPRINT_PIXELS = [
  'X...X....',
  'XX...XX..',
  '.XX...XX.',
  '..XX...XX',
  '...XX...X',
  '..XX...XX',
  '.XX...XX.',
  'XX...XX..',
  'X...X....',
]

export interface TouchState {
  forward: number
  strafe: number
  jump: boolean
  crouch: boolean
  /** Minecraft's sprint toggle: the button, or a double-tap on forward. Clears when you stop. */
  sprint: boolean
  attack: boolean
  /** Kate: "put the burrow icon where the controls are so I don't have to go into settings." */
  dig: boolean
  /** "There needs to be a sleep icon." Only useful at night. */
  sleep: boolean
  /** The animal's one special move — freeze, trample, soar, pounce, sniff, graze. */
  verb: boolean
}

export class TouchControls {
  readonly root: HTMLDivElement
  readonly state: TouchState = { forward: 0, strafe: 0, jump: false, crouch: false, sprint: false,
                                 attack: false, dig: false, sleep: false, verb: false }

  private readonly look: LookControl
  private pad!: HTMLDivElement
  private readonly padCells = new Map<PadDir, HTMLDivElement>()
  private sprintButton!: HTMLButtonElement
  private sneakButton!: HTMLButtonElement
  private sleepButton!: HTMLButtonElement
  private verbButton!: HTMLButtonElement
  private digButton!: HTMLButtonElement
  private padTouchId: number | null = null
  private padDir: PadDir | null = null
  private lastForwardTap = -Infinity
  private lookTouchId: number | null = null
  private lookLast = { x: 0, y: 0 }

  constructor(look: LookControl, store: SettingsStore) {
    this.look = look

    this.root = document.createElement('div')
    this.root.className = 'touch'
    this.build()
    document.body.append(this.root)

    this.apply(store.current)
    store.subscribe((s) => this.apply(s))

    // Looking is a drag anywhere that isn't a control.
    this.root.addEventListener('touchstart', this.onLookStart, { passive: false })
    window.addEventListener('touchmove', this.onTouchMove, { passive: false })
    window.addEventListener('touchend', this.onTouchEnd)
    window.addEventListener('touchcancel', this.onTouchEnd)
  }

  /**
   * Show the controls only where they're usable. Coarse pointer covers iPad and phone;
   * `maxTouchPoints` catches touch laptops that also report a fine pointer.
   */
  static isTouchDevice(): boolean {
    return window.matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0
  }

  setVisible(visible: boolean): void {
    this.root.hidden = !visible
  }

  /**
   * Kate, 2026-08-23: "there needs to be a button instead of burrow. This is only if
   * you're a bird that says nest." A bird digging a hole in the ground never made
   * sense; it nests in a tree.
   */
  setDigLabel(kind: 'burrow' | 'nest' | 'none'): void {
    // Kate, 2026-08-24: "you should only have nest and fly if you're a bird... if
    // you're not a bird or a human... instead you have burrow." A human sleeps
    // anywhere, so a human has no reason to dig and no button for it.
    this.digButton.hidden = kind === 'none'
    if (kind === 'none') return
    this.digButton.innerHTML = kind === 'nest'
      ? '<span class="touch-glyph">🪹</span><span>NEST</span>'
      : '<span class="touch-glyph">🕳️</span><span>BURROW</span>'
  }

  /** Label the verb button for the current animal, or hide it if it has none. */
  setVerb(label: string | null): void {
    this.verbButton.hidden = label === null
    if (label) this.verbButton.innerHTML = `<span>${label}</span>`
  }

  /** Sleep only means anything at night, so the button dims outside it. */
  setNight(isNight: boolean): void {
    this.sleepButton.classList.toggle('is-dim', !isNight)
  }

  private build(): void {
    // --- D-pad: nine squares, arrows on the four cardinals. The top corners are blank
    // until forward is held, then they become the forward-diagonals. ---
    this.pad = document.createElement('div')
    this.pad.className = 'touch-pad'
    for (const dir of PAD_CELLS) {
      const cell = document.createElement('div')
      cell.className = 'touch-pad-cell'
      if (dir) {
        cell.dataset.dir = dir
        cell.innerHTML = padIcon(dir)
        this.padCells.set(dir, cell)
      }
      this.pad.append(cell)
    }
    this.pad.addEventListener('touchstart', this.onPadStart, { passive: false })
    this.root.append(this.pad)

    // --- Jump / sprint / sneak, stacked at the other thumb ---
    const column = document.createElement('div')
    column.className = 'touch-column'
    const jump = document.createElement('button')
    jump.className = 'touch-button touch-jump'
    jump.setAttribute('aria-label', 'Jump')
    jump.innerHTML = arrowIcon(0)
    this.bindHold(jump, 'jump')
    this.sprintButton = document.createElement('button')
    this.sprintButton.className = 'touch-button touch-sprint'
    this.sprintButton.setAttribute('aria-label', 'Sprint')
    this.sprintButton.innerHTML = sprintIcon()
    this.bindTap(this.sprintButton, () => this.setSprint(!this.state.sprint))
    this.sneakButton = document.createElement('button')
    this.sneakButton.className = 'touch-button touch-sneak'
    this.sneakButton.setAttribute('aria-label', 'Sneak')
    this.sneakButton.innerHTML = arrowIcon(180)
    this.bindTap(this.sneakButton, () => this.setSneak(!this.state.crouch))
    column.append(jump, this.sprintButton, this.sneakButton)
    this.root.append(column)

    // --- Kate's buttons, beside the column, in the same squares. ATTACK is the big one. ---
    const actions = document.createElement('div')
    actions.className = 'touch-actions'
    const row = document.createElement('div')
    row.className = 'touch-actions-row'

    this.sleepButton = document.createElement('button')
    this.sleepButton.className = 'touch-button touch-sleep'
    this.sleepButton.innerHTML = '<span class="touch-glyph">😴</span><span>SLEEP</span>'
    this.bindHold(this.sleepButton, 'sleep')

    // Always on the controls, never in a menu — her complaint was having to go digging
    // through settings on an iPad every time.
    this.digButton = document.createElement('button')
    this.digButton.className = 'touch-button touch-dig'
    this.setDigLabel('burrow')
    this.bindHold(this.digButton, 'dig')

    // The animal's special move. Labelled per species, hidden for animals without one.
    this.verbButton = document.createElement('button')
    this.verbButton.className = 'touch-button touch-verb'
    this.bindHold(this.verbButton, 'verb')
    row.append(this.sleepButton, this.digButton, this.verbButton)

    // ATTACK: labelled button with a chomp icon.
    const attack = document.createElement('button')
    attack.className = 'touch-button touch-attack'
    attack.innerHTML = `${chompIcon()}<span>ATTACK</span>`
    this.bindHold(attack, 'attack')
    actions.append(row, attack)
    this.root.append(actions)
  }

  /** Wire a button so the flag is true only while it is held. */
  private bindHold(element: HTMLElement, key: 'jump' | 'attack' | 'dig' | 'sleep' | 'verb'): void {
    const down = (event: Event): void => {
      event.preventDefault()
      event.stopPropagation()
      this.state[key] = true
      element.classList.add('is-held')
    }
    const up = (event: Event): void => {
      event.preventDefault()
      this.state[key] = false
      element.classList.remove('is-held')
    }
    element.addEventListener('touchstart', down, { passive: false })
    element.addEventListener('touchend', up)
    element.addEventListener('touchcancel', up)
    // Mouse fallback so the controls are testable and usable on a touch laptop.
    element.addEventListener('mousedown', down)
    window.addEventListener('mouseup', up)
  }

  /** Wire a toggle: one tap flips it. */
  private bindTap(element: HTMLElement, tap: () => void): void {
    const down = (event: Event): void => {
      event.preventDefault()
      event.stopPropagation()
      tap()
    }
    element.addEventListener('touchstart', down, { passive: false })
    element.addEventListener('mousedown', down)
  }

  /** Sprint and sneak are exclusive, as in Minecraft: starting one ends the other. */
  private setSprint(on: boolean): void {
    if (on) this.setSneak(false)
    this.state.sprint = on
    this.sprintButton.classList.toggle('is-on', on)
  }

  /** Sneak is a toggle, as in Minecraft, and it ends a sprint — you can't do both. */
  private setSneak(on: boolean): void {
    this.state.crouch = on
    this.sneakButton.classList.toggle('is-on', on)
    if (on) this.setSprint(false)
  }

  private apply(settings: Settings): void {
    this.root.dataset.hand = settings.touchHandedness
    // Also on <body>: the HUD readouts keep clear of the pad using the same two values.
    document.body.dataset.hand = settings.touchHandedness
    document.body.style.setProperty('--touch-scale', String(settings.touchScale))
  }

  // ---- D-pad ----

  private readonly onPadStart = (event: TouchEvent): void => {
    event.preventDefault()
    event.stopPropagation()
    const touch = event.changedTouches[0]
    if (!touch || this.padTouchId !== null) return
    this.padTouchId = touch.identifier
    this.setPadDir(this.cellAt(touch.clientX, touch.clientY))
    // Double-tap forward sprints, as in Minecraft.
    if (this.padDir === 'f') {
      const now = performance.now()
      if (now - this.lastForwardTap < DOUBLE_TAP_MS) this.setSprint(true)
      this.lastForwardTap = now
    }
  }

  /** Which square is under a point. Past the pad's edge counts as the nearest square. */
  private cellAt(x: number, y: number): PadDir | null {
    const rect = this.pad.getBoundingClientRect()
    const col = Math.max(0, Math.min(2, Math.floor(((x - rect.left) / rect.width) * 3)))
    const row = Math.max(0, Math.min(2, Math.floor(((y - rect.top) / rect.height) * 3)))
    return PAD_CELLS[row * 3 + col]
  }

  /**
   * Press one square. The diagonals only exist while forward is held — until then they
   * are blank, exactly as on the Minecraft pad — and a sprint ends when you leave
   * forward, by a slide or a release, or when you stop moving at all.
   */
  private setPadDir(dir: PadDir | null): void {
    const wasForward = this.state.forward > 0
    const wasMoving = this.state.forward !== 0 || this.state.strafe !== 0
    if ((dir === 'fl' || dir === 'fr') && !wasForward) dir = null
    this.padDir = dir
    const vector = dir ? PAD_VECTORS[dir] : { forward: 0, strafe: 0 }
    this.state.forward = vector.forward
    this.state.strafe = vector.strafe
    const moving = vector.forward !== 0 || vector.strafe !== 0
    if ((wasForward && vector.forward <= 0) || (wasMoving && !moving)) this.setSprint(false)
    for (const [d, cell] of this.padCells) cell.classList.toggle('is-held', d === dir)
    this.pad.classList.toggle('is-forward', vector.forward > 0)
  }

  private releasePad(): void {
    this.padTouchId = null
    this.setPadDir(null)
  }

  // ---- Look drag ----

  private readonly onLookStart = (event: TouchEvent): void => {
    if (this.lookTouchId !== null) return
    const touch = event.changedTouches[0]
    if (!touch) return
    // Only the bare backdrop starts a look drag; the pad and buttons stop propagation.
    if (event.target !== this.root) return
    this.lookTouchId = touch.identifier
    this.lookLast = { x: touch.clientX, y: touch.clientY }
  }

  private readonly onTouchMove = (event: TouchEvent): void => {
    for (const touch of Array.from(event.changedTouches)) {
      if (touch.identifier === this.padTouchId) {
        event.preventDefault()
        // Sliding across the pad moves the press with the thumb, without lifting.
        this.setPadDir(this.cellAt(touch.clientX, touch.clientY))
      } else if (touch.identifier === this.lookTouchId) {
        event.preventDefault()
        this.look.applyDelta(touch.clientX - this.lookLast.x, touch.clientY - this.lookLast.y)
        this.lookLast = { x: touch.clientX, y: touch.clientY }
      }
    }
  }

  private readonly onTouchEnd = (event: TouchEvent): void => {
    for (const touch of Array.from(event.changedTouches)) {
      if (touch.identifier === this.padTouchId) this.releasePad()
      if (touch.identifier === this.lookTouchId) this.lookTouchId = null
    }
  }
}

/** A 9×9 pixel grid as an SVG, one rect per run of pixels, 4 units to a pixel. */
function pixelIcon(rows: readonly string[], transform = ''): string {
  const rects: string[] = []
  rows.forEach((row, y) => {
    for (let x = 0; x < row.length; x++) {
      if (row[x] !== 'X') continue
      let width = 1
      while (row[x + width] === 'X') width++
      rects.push(`<rect x="${x * 4}" y="${y * 4}" width="${width * 4}" height="4"/>`)
      x += width - 1
    }
  })
  return `<svg viewBox="0 0 36 36" aria-hidden="true" class="touch-icon" fill="currentColor"
       shape-rendering="crispEdges"><g transform="${transform}">${rects.join('')}</g></svg>`
}

/** A white pixel arrow, pointing up, rotated to suit. */
function arrowIcon(angle: number): string {
  return pixelIcon(ARROW_PIXELS, `rotate(${angle} 18 18)`)
}

/** The arrow for one pad square: a cardinal, or the diagonal and its mirror. */
function padIcon(dir: PadDir): string {
  if (dir === 'fr') return pixelIcon(DIAGONAL_PIXELS)
  if (dir === 'fl') return pixelIcon(DIAGONAL_PIXELS, 'matrix(-1 0 0 1 36 0)')
  return arrowIcon(PAD_ANGLES[dir])
}

/** Minecraft's sprint glyph: a double chevron. */
function sprintIcon(): string {
  return pixelIcon(SPRINT_PIXELS)
}

/** A chomp — open jaws with teeth. Kate asked for "a little chomp icon". */
function chompIcon(): string {
  return `<svg viewBox="0 0 32 32" aria-hidden="true" class="touch-chomp">
    <path d="M3 9 A14 14 0 0 1 29 9 L16 14 Z" fill="currentColor"/>
    <path d="M3 23 A14 14 0 0 0 29 23 L16 18 Z" fill="currentColor"/>
    <path d="M8 10 L10 13.2 L12.4 10.9 Z M14 10.6 L15.6 13.6 L18 11 Z M20.6 10.4 L21.6 13 L24 10 Z"
          fill="#12200d"/>
    <path d="M8 22 L10 18.8 L12.4 21.1 Z M14 21.4 L15.6 18.4 L18 21 Z M20.6 21.6 L21.6 19 L24 22 Z"
          fill="#12200d"/>
  </svg>`
}
